/**
 * Responsive download-buttons toolbar for Captivate learning, documents, and hot links.
 *
 * @module components/common/DownloadButtons
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
    Button,
    List,
    ListItem,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
} from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';
import GetAppIcon from '@material-ui/icons/GetApp';
import ResponsiveDrawerStyle from './ResponsiveDrawer';
import { saveCaptivateWindowRef } from '../../redux/sims/actions';


/**
 * A component that renders download-related buttons and modals based on the available data and user interactions.
 *
 * @param {Object} props - The props for the component.
 * @param {boolean} props.showBtn - Determines whether the button should be displayed.
 * @param {Function} props.handleBtnToggle - Callback function to toggle the visibility of the buttons.
 * @param {Function} props.saveCaptivateWindowRef - Function to save a reference to the Captivate Learning window.
 *
 * The component dynamically renders buttons and drawers for different user actions.
 * It supports opening a new window for Captivate Learning, displaying downloadable documents,
 * showing hot links, and rendering Captivate demo.
 */
const DownloadButtons = ({
    showBtn,
    handleBtnToggle,
    saveCaptivateWindowRef,
    captivate_learning_video_url,
    captivate_learning_url,
    documents,
    hotLinks,
}) => {
    const useStyles = makeStyles((theme) => ResponsiveDrawerStyle(theme));
    const theme = useTheme();
    const classes = useStyles();
    const [openDocuments, setOpenDocuments] = React.useState(false);
    const [openLinks, setOpenLinks] = React.useState(false);
    const [openCaptivateDemo, setOpenCaptivateDemo] = React.useState(false);
    let captivateLearningWindow;

    /**
     * Opens a new browser window for the Captivate learning platform.
     *
     * This function retrieves the Captivate learning URL from the application's state
     * and opens it in a new browser window with specified configurations such as size,
     * position, and toolbar visibility. The created window reference is saved for further use.
     * Use `captivateLearningWindow.close()` to close the opened window when necessary.
     *
     * Note: The `window.open` method is used to create the new window, and it must be invoked
     * within a user-initiated event (e.g., click) in most modern browsers due to restrictions
     * on automated pop-ups.
     */
    const openCaptivateNewWindow = () => {
        captivateLearningWindow = window.open(
            captivate_learning_url,
            '_blank',
            'toolbar=yes, scrollbars=yes, resizable=yes,top=500, left=500, width=900, height=900, titlebar=no, toolbar=no, location=no'
        );
        saveCaptivateWindowRef(captivateLearningWindow);
    };

    return (
        <>
            <Hidden className={classes.hidden} smUp implementation="css">
                <Drawer
                    variant="temporary"
                    anchor={theme.direction === 'rtl' ? 'bottom' : 'top'}
                    open={showBtn}
                    onClose={handleBtnToggle}
                    classes={{
                        paper: classes.drawerBtnPaper,
                    }}
                    ModalProps={{
                        keepMounted: true,
                    }}
                >
                    <List className={classes.list}>
                        {captivate_learning_video_url
                            && (
                                <ListItem
                                    className={classes.listItem}
                                    onClick={() => setOpenCaptivateDemo(true)}
                                >
                                    CAPTIVATE DEMO
                                </ListItem>
                            )}
                        {captivate_learning_url
                            && (
                                <ListItem
                                    className={classes.listItem}
                                    onClick={openCaptivateNewWindow}
                                >
                                    CAPTIVATE LEARNING
                                </ListItem>
                            )}
                        {documents && documents.length
                            ? (
                                <ListItem
                                    className={classes.listItem}
                                    onClick={() => setOpenDocuments(true)}
                                >
                                    DOWNLOAD DOCUMENTS
                                </ListItem>
                            )
                            : ''}
                        {hotLinks && hotLinks.length
                            ? (
                                <ListItem
                                    className={classes.listItem}
                                    onClick={() => setOpenLinks(true)}
                                >
                                    HOT LINK URL(s)
                                </ListItem>
                            )
                            : '' }
                    </List>
                </Drawer>
            </Hidden>
            {!showBtn
                && (
                    <div className={classes.downloadBtn}>
                        {captivate_learning_video_url
                            && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    disableElevation
                                    className={classes.downloadButtons}
                                    onClick={() => setOpenCaptivateDemo(true)}
                                >
                                    CAPTIVATE DEMO
                                </Button>
                            )}
                        {captivate_learning_url
                            && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    disableElevation
                                    className={classes.downloadButtons}
                                    onClick={openCaptivateNewWindow}
                                >
                                    CAPTIVATE LEARNING
                                </Button>
                            )}
                        {documents && documents.length
                            ? (
                                <Button
                                    variant="contained"
                                    size="small"
                                    disableElevation
                                    className={classes.downloadButtons}
                                    onClick={() => setOpenDocuments(true)}
                                >
                                    DOWNLOAD DOCUMENTS
                                </Button>
                            )
                            : ''}
                        {hotLinks && hotLinks.length
                            ? (
                                <Button
                                    variant="contained"
                                    size="small"
                                    disableElevation
                                    className={classes.downloadButtons}
                                    onClick={() => setOpenLinks(true)}
                                >
                                    Hot Link URL(s)
                                </Button>
                            )
                            : ''}
                    </div>
                )}

            <Dialog
                open={openDocuments}
                onClose={() => { setOpenDocuments(false); }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Download Documents</DialogTitle>
                <DialogContent>
                    <List>
                        {documents && documents.map((doc, index) => (
                            <ListItem key={doc._id}>
                                <ListItemText>
                                    {index + 1}
                                    .
                                    {' '}
                                    {doc.name}
                                </ListItemText>
                                <ListItemSecondaryAction className={classes.listAction}>
                                    <IconButton href={doc.document_url} target="_blank">
                                        {' '}
                                        {' '}
                                        <GetAppIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenDocuments(false)}
                        className={classes.cancelButton}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={openLinks}
                onClose={() => { setOpenLinks(false); }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Hot Link URL(s)</DialogTitle>
                <DialogContent>
                    <List>
                        {hotLinks && hotLinks.map((link, index) => (
                            <ListItem key={index} component="a" href={link} target="_blank">
                                <ListItemText title={link.length > 55 ? link : null}>
                                    {index + 1}
                                    .
                                    {' '}
                                    {link.length > 55
                                        ? `${link.slice(0, 55)}  ...`
                                        : link}
                                </ListItemText>
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenLinks(false)}
                        className={classes.cancelButton}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={openCaptivateDemo}
                onClose={() => { setOpenCaptivateDemo(false); }}
                maxWidth="lg"
            >
                <DialogTitle>Captivate Learning Demo Video</DialogTitle>
                <DialogContent>
                    <video
                        width="1000"
                        height="700"
                        controls
                        autoPlay
                    >
                        <source src={captivate_learning_video_url} type="video/mp4" />
                    </video>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenCaptivateDemo(false)}
                        className={classes.cancelButton}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

DownloadButtons.propTypes = {
    handleBtnToggle: PropTypes.func,
    showBtn: PropTypes.bool,
    saveCaptivateWindowRef: PropTypes.func,
    captivate_learning_video_url: PropTypes.string,
    captivate_learning_url: PropTypes.string,
    documents: PropTypes.array,
    hotLinks: PropTypes.array,
};

/**
 * @param {Object} state - Redux state.
 * @returns {Object} Props derived from the sims slice of state.
 */
const mapStateToProps = (state) => ({
    captivate_learning_video_url: state.sims?.captivate_learning_video_url,
    captivate_learning_url: state.sims?.captivate_learning_url,
    documents: state.sims?.documents,
    hotLinks: state.sims?.hotLinks,
});

const mapDispatchToProps = {
    saveCaptivateWindowRef,
};

export default connect(mapStateToProps, mapDispatchToProps)(DownloadButtons);
